// TableView.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useUser } from 'context/UserContext';

// Utilities
import { buildEnumOptionValueRankMap } from 'utilities/EnumOptions';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { getSingularLabel } from 'utilities/Languages';
import { getNodelistPrimaryKey } from 'utilities/NodelistField';
import { buildTableColumns } from 'views/table/utils/tableColumnUtils';
import { getFieldType, resolveFieldValue } from 'views/table/utils/tableFieldUtils';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import useTableActions from 'views/table/hooks/useTableActions';
import useTableColumnReorder from 'views/table/hooks/useTableColumnReorder';
import useTableColumnSizing from 'views/table/hooks/useTableColumnSizing';
import useTableEditState from 'views/table/hooks/useTableEditState';
import useTableHandlers from 'views/table/hooks/useTableHandlers';
import useTableNodelistHydration from 'views/table/hooks/useTableNodelistHydration';
import useTableSchema from 'views/table/hooks/useTableSchema';

// Constants
import { VIEW_PAGINATION } from 'views/constants/viewPagination';

// CSS
import './TableView.css';

// Components
import TableBody from 'views/table/components/TableBody';
import TableFooter from 'views/table/components/TableFooter';
import TableHeader from 'views/table/components/TableHeader';
import TableModals from 'views/table/components/TableModals';

const TABLE_EXCLUDED_FIELD_TYPES = ['cover', 'comments', 'activity', 'location', 'tags'];
const TABLE_NON_SORTABLE_FIELD_TYPES = ['checklist', 'comments', 'cover', 'location', 'measurement', 'people', 'tags'];

const SORT_DIRECTION = Object.freeze({
    ASC: 'asc',
    DESC: 'desc',
});

const resolveComparableValue = (value, fieldType) => {
    if (value == null || value === '') {
        return { empty: true, value: null };
    }

    if (fieldType === 'boolean') {
        return { empty: false, value: value ? 1 : 0 };
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return { empty: false, value };
    }

    if (value instanceof Date && Number.isFinite(value.getTime())) {
        return { empty: false, value: value.getTime() };
    }

    const timestampSeconds = Number(value?.seconds);
    const timestampNanos = Number(value?.nanoseconds);
    if (Number.isFinite(timestampSeconds)) {
        const nanos = Number.isFinite(timestampNanos) ? timestampNanos : 0;
        return { empty: false, value: timestampSeconds * 1000 + Math.floor(nanos / 1e6) };
    }

    const numeric = Number(value);
    if (Number.isFinite(numeric) && (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage')) {
        return { empty: false, value: numeric };
    }

    const text = typeof value === 'string' ? value : String(value);
    const dateLike = Date.parse(text);
    if (Number.isFinite(dateLike) && (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'time')) {
        return { empty: false, value: dateLike };
    }

    return { empty: false, value: text.toLocaleLowerCase() };
};

const compareSortValues = (leftValue, rightValue) => {
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
    return 0;
};

/**
 * TableView
 * 
 * Renders a table view.
 * 
 * @param {object} view - The view object containing type and other properties.
 * @param {object} typeDef - The type definition for the nodes.
 * @param {object[]} nodes - The array of nodes to display.
 * @param {string} addTypeKey - Optional type key to use for adding new nodes, if different from typeDef.key.
 * @param {function} onQuickActionSelect - Optional callback function to call when a quick action is selected, called with (actionKey, node).
 * @param {function} onQuickActionCreateOnly - Optional callback function to call when a quick action that creates a node is selected, called with (actionKey).
 * @param {Set|string[]} selectedKeys - The set or array of currently selected node keys.
 * @param {function} setSelectedKeys - Function to update the selected keys, called with (newSelectedKeys).
 * @param {string} displayMode - The display mode for the view, either 'full' or 'compact'.
 * 
 * @returns {ReactNode} - TableView.
 */
const TableView = React.memo(({
    view,
    typeDef,
    nodes = [],
    addTypeKey,
    onQuickActionCreateOnly,
    onQuickActionSelect,
    selectedKeys,
    setSelectedKeys,
    showCheckboxes = true,
    readOnly = false,
    nodeDragEnabled = false,
}) => {

    // ----- REFS ----- //

    const headerScrollRef = React.useRef(null);
    const bodyScrollRef = React.useRef(null);
    const syncSourceRef = React.useRef(null);

    // ----- CONTEXT ----- //

    const language = useCurrentLanguage();
    const { user } = useUser();

    // ----- HOOKS ----- //

    const { openNode } = useNodeOpenIntent();

    const handleNodeFieldChange = useCallback(async (nodeKey, patch) => {
        if (readOnly || !nodeKey || !patch) return;
        try {
            await updateNode(nodeKey, patch, { actor: user });
        } catch (error) {
            console.error('[TableView] update field error:', error);
        }
    }, [readOnly, user]);

    // ----- STATE ----- //

    const [displayCount, setDisplayCount] = useState(VIEW_PAGINATION.TABLE.INITIAL);
    const [headerSort, setHeaderSort] = useState({
        fieldKey: null,
        direction: null,
    });

    // ----- HOOKS ----- //

    const {
        hasExplicitProperties,
        schemaFieldMap,
        visibleFieldKeys,
        fieldDataSection,
    } = useTableSchema({
        typeDef,
        view,
    });

    // ----- MEMOS ----- //

    const filteredVisibleFieldKeys = useMemo(() => (
        (visibleFieldKeys || []).filter((fieldKey) => {
            const fieldType = String(schemaFieldMap?.get(fieldKey)?.type || '').toLowerCase();
            return !TABLE_EXCLUDED_FIELD_TYPES.includes(fieldType);
        })
    ), [visibleFieldKeys, schemaFieldMap]);

    // ----- HOOKS ----- //

    const { effectiveColumnWidths, handleColumnResizeStart } = useTableColumnSizing({
        view,
        visibleFieldKeys: filteredVisibleFieldKeys,
    });

    const {
        editingCell,
        editingValue,
        setEditingValue,
        handleStartEdit,
        handleCancelEdit,
        handleCommitEdit,
    } = useTableEditState({
        typeDefKey: typeDef?.key,
        fieldDataSection,
        typeDef,
    });

    const {
        modalState,
        handleModalOpen,
        handleModalClose,
        handleDateChange,
        handleEnumChange,
        handleTagsSaved,
        handleLocationSaved,
        handlePeopleSaved,
        handleNodelistSaved,
        handlePercentageChange,
        fieldModalOpen,
        fieldSaving,
        handleFieldModalOpen,
        handleFieldSave,
    } = useTableActions({
        view,
        typeDef,
        nodes,
        visibleFieldKeys: filteredVisibleFieldKeys,
        hasExplicitProperties,
    });

    const {
        handleAddRow,
        handleCellActivate,
        handleOpenNode,
    } = useTableHandlers({
        openNode,
        addTypeKey,
        onQuickActionCreateOnly,
        onQuickActionSelect,
        handleStartEdit: readOnly ? () => {} : handleStartEdit,
        handleModalOpen: readOnly ? () => {} : handleModalOpen,
        typeDef,
    });

    const effectiveHandleCellActivate = readOnly ? () => {} : handleCellActivate;

    const {
        orderedFieldKeys,
        draggingKey,
        dragOverKey,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragEnd,
    } = useTableColumnReorder({
        view,
        visibleFieldKeys: filteredVisibleFieldKeys,
        resetKey: `${view?.key || ''}:${typeDef?.key || ''}`,
    });

    const { resolveNodelistTitle } = useTableNodelistHydration({
        nodes,
        schemaFieldMap,
        fieldKeys: orderedFieldKeys,
        fieldDataSection,
    });

    // ----- MEMOS ----- //

    /**
     * columns
     * 
     * Defines table columns.
     *  
     * @returns {Array} - Array of column definitions.
     */

    const columns = useMemo(() => (
        buildTableColumns({
            schemaFieldMap,
            visibleFieldKeys: orderedFieldKeys,
            effectiveColumnWidths,
        })
    ), [schemaFieldMap, orderedFieldKeys, effectiveColumnWidths]);

    const sortableColumnMap = useMemo(() => {
        const map = new Map();
        (columns || []).forEach((column) => {
            const fieldDef = schemaFieldMap?.get(column.key) || { name: column.key };
            const fieldType = getFieldType(fieldDef, column.key);
            map.set(column.key, !TABLE_NON_SORTABLE_FIELD_TYPES.includes(fieldType));
        });
        return map;
    }, [columns, schemaFieldMap]);

    const headerColumns = useMemo(() => (
        (columns || [])
            .filter((column) => column.key !== 'open')
            .map((column) => ({
                ...column,
                isSortable: Boolean(sortableColumnMap.get(column.key)),
            }))
    ), [columns, sortableColumnMap]);

    const sortedNodes = useMemo(() => {
        const sourceNodes = Array.isArray(nodes) ? nodes : [];
        const sortFieldKey = headerSort?.fieldKey;
        const sortDirection = headerSort?.direction;

        if (!sortFieldKey || !sortDirection) {
            return sourceNodes;
        }

        if (!sortableColumnMap.get(sortFieldKey)) {
            return sourceNodes;
        }

        const sortFieldDef = schemaFieldMap?.get(sortFieldKey) || { name: sortFieldKey };
        const sortFieldType = getFieldType(sortFieldDef, sortFieldKey);
        const directionMultiplier = sortDirection === SORT_DIRECTION.DESC ? -1 : 1;
        const enumValueRanks = isEnumCompatibleFieldType(sortFieldType)
            ? buildEnumOptionValueRankMap(sortFieldDef?.options || [])
            : null;

        return sourceNodes
            .map((node, index) => ({ node, index }))
            .sort((left, right) => {
                const leftRaw = resolveFieldValue(left.node, sortFieldDef, fieldDataSection);
                const rightRaw = resolveFieldValue(right.node, sortFieldDef, fieldDataSection);

                if (isEnumCompatibleFieldType(sortFieldType)) {
                    const leftValue = leftRaw == null ? '' : String(leftRaw).trim();
                    const rightValue = rightRaw == null ? '' : String(rightRaw).trim();

                    if (!leftValue && !rightValue) {
                        return left.index - right.index;
                    }
                    if (!leftValue) {
                        return 1;
                    }
                    if (!rightValue) {
                        return -1;
                    }

                    const leftHasRank = enumValueRanks.has(leftValue);
                    const rightHasRank = enumValueRanks.has(rightValue);

                    if (leftHasRank && rightHasRank) {
                        const rankCompare = compareSortValues(
                            enumValueRanks.get(leftValue),
                            enumValueRanks.get(rightValue)
                        );
                        if (rankCompare !== 0) {
                            return rankCompare * directionMultiplier;
                        }
                    } else if (leftHasRank !== rightHasRank) {
                        return leftHasRank ? -1 : 1;
                    }

                    const valueCompare = leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' });
                    if (valueCompare !== 0) {
                        return valueCompare * directionMultiplier;
                    }

                    return left.index - right.index;
                }

                if (sortFieldType === 'relation') {
                    const leftKey = getNodelistPrimaryKey(leftRaw);
                    const rightKey = getNodelistPrimaryKey(rightRaw);
                    const leftValue = leftKey ? String(resolveNodelistTitle(leftKey) || leftKey) : '';
                    const rightValue = rightKey ? String(resolveNodelistTitle(rightKey) || rightKey) : '';

                    if (!leftValue && !rightValue) {
                        return left.index - right.index;
                    }
                    if (!leftValue) {
                        return 1;
                    }
                    if (!rightValue) {
                        return -1;
                    }

                    const valueCompare = leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' });
                    if (valueCompare !== 0) {
                        return valueCompare * directionMultiplier;
                    }

                    return left.index - right.index;
                }

                const leftComparable = resolveComparableValue(leftRaw, sortFieldType);
                const rightComparable = resolveComparableValue(rightRaw, sortFieldType);

                if (leftComparable.empty && rightComparable.empty) {
                    return left.index - right.index;
                }
                if (leftComparable.empty) {
                    return 1;
                }
                if (rightComparable.empty) {
                    return -1;
                }

                const compareResult = compareSortValues(leftComparable.value, rightComparable.value);
                if (compareResult !== 0) {
                    return compareResult * directionMultiplier;
                }

                return left.index - right.index;
            })
            .map(({ node }) => node);
    }, [nodes, headerSort, sortableColumnMap, schemaFieldMap, fieldDataSection, resolveNodelistTitle]);

    const displayedNodes = useMemo(() => (
        sortedNodes.slice(0, displayCount)
    ), [sortedNodes, displayCount]);

    const hasMore = displayedNodes.length < sortedNodes.length;
    const nodeCount = sortedNodes.length;

    const selectableKeys = useMemo(() => (
        (displayedNodes || [])
            .map((node) => node?.key)
            .filter(Boolean)
    ), [displayedNodes]);
    const resolvedSelectedKeys = selectedKeys instanceof Set ? selectedKeys : new Set();
    const allSelected = selectableKeys.length > 0
        && selectableKeys.every((key) => resolvedSelectedKeys.has(key));

    // ----- HANDLERS ----- //

    const handleToggleSelect = React.useCallback((nodeKey) => {
        if (!nodeKey || !setSelectedKeys) return;
        setSelectedKeys((prev) => {
            const next = new Set(prev instanceof Set ? prev : []);
            if (next.has(nodeKey)) {
                next.delete(nodeKey);
            } else {
                next.add(nodeKey);
            }
            return next;
        });
    }, [setSelectedKeys]);

    const handleToggleAll = React.useCallback(() => {
        if (!setSelectedKeys || selectableKeys.length === 0) return;
        setSelectedKeys((prev) => {
            const next = new Set(prev instanceof Set ? prev : []);
            if (allSelected) {
                selectableKeys.forEach((key) => next.delete(key));
            } else {
                selectableKeys.forEach((key) => next.add(key));
            }
            return next;
        });
    }, [setSelectedKeys, allSelected, selectableKeys]);

    const singularLabel = getSingularLabel(
        typeDef,
        addTypeKey || typeDef?.key || 'NODE',
        language
    );
    const addLabel = `+ Add ${singularLabel || 'Node'}`;

    // ----- EFFECTS ----- //

    useEffect(() => {
        setDisplayCount(VIEW_PAGINATION.TABLE.INITIAL);
    }, [view?.key, typeDef?.key]);

    useEffect(() => {
        const sortFieldKey = headerSort?.fieldKey;
        if (!sortFieldKey) return;
        if (!sortableColumnMap.get(sortFieldKey)) {
            setHeaderSort({
                fieldKey: null,
                direction: null,
            });
        }
    }, [headerSort, sortableColumnMap]);

    useEffect(() => {
        const total = nodeCount;
        setDisplayCount((prev) => {
            if (total <= 0) return VIEW_PAGINATION.TABLE.INITIAL;
            if (prev > total) return total;
            return prev;
        });
    }, [nodeCount]);

    // ----- HANDLERS ----- //

    const handleLoadMore = React.useCallback(() => {
        setDisplayCount((prev) => Math.min(prev + VIEW_PAGINATION.TABLE.STEP, sortedNodes.length));
    }, [sortedNodes.length]);

    const handleSortAscending = React.useCallback((fieldKey) => {
        if (!fieldKey || !sortableColumnMap.get(fieldKey)) return;
        setHeaderSort({
            fieldKey,
            direction: SORT_DIRECTION.ASC,
        });
    }, [sortableColumnMap]);

    const handleSortDescending = React.useCallback((fieldKey) => {
        if (!fieldKey || !sortableColumnMap.get(fieldKey)) return;
        setHeaderSort({
            fieldKey,
            direction: SORT_DIRECTION.DESC,
        });
    }, [sortableColumnMap]);

    const handleHeaderScroll = React.useCallback(() => {
        const headerEl = headerScrollRef.current;
        const bodyEl = bodyScrollRef.current;
        if (!headerEl || !bodyEl) return;

        if (syncSourceRef.current === 'body') {
            syncSourceRef.current = null;
            return;
        }

        if (bodyEl.scrollLeft !== headerEl.scrollLeft) {
            syncSourceRef.current = 'header';
            bodyEl.scrollLeft = headerEl.scrollLeft;
        }
    }, []);

    const handleBodyScroll = React.useCallback(() => {
        const headerEl = headerScrollRef.current;
        const bodyEl = bodyScrollRef.current;
        if (!headerEl || !bodyEl) return;

        if (syncSourceRef.current === 'header') {
            syncSourceRef.current = null;
            return;
        }

        if (headerEl.scrollLeft !== bodyEl.scrollLeft) {
            syncSourceRef.current = 'body';
            headerEl.scrollLeft = bodyEl.scrollLeft;
        }
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const headerEl = headerScrollRef.current;
        const bodyEl = bodyScrollRef.current;
        if (!headerEl || !bodyEl) return;
        headerEl.scrollLeft = bodyEl.scrollLeft;
    }, [view?.key, typeDef?.key]);
    // ----- RENDER ----- //

    return (

        <div className="table-view-container">

            {/* HEADER SCROLLER */}
            <div
              className="table-view-header-scroll"
              ref={headerScrollRef}
              onScroll={handleHeaderScroll}
            >

                {/* HEADER CONTENT */}
                <div className="table-view-header-content">

                    <TableHeader
                      showCheckboxes={showCheckboxes}
                      columns={headerColumns}
                      allSelected={allSelected}
                      draggingKey={draggingKey}
                      dragOverKey={dragOverKey}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragStart={handleDragStart}
                      onDrop={handleDrop}
                      onResizeStart={handleColumnResizeStart}
                      onSortAscending={handleSortAscending}
                      onSortDescending={handleSortDescending}
                      onToggleAll={handleToggleAll}
                    />

                </div>

            </div>

            {/* BODY SCROLL */}
            <div
              className="table-view-body-scroll"
              ref={bodyScrollRef}
              onScroll={handleBodyScroll}
            >

                {/* TABLE*/}

                {/* BODY CONTENT */}
                <div className="table-view-body-content">

                    <TableBody
                      nodes={displayedNodes}
                      columns={columns}
                      schemaFieldMap={schemaFieldMap}
                      fieldDataSection={fieldDataSection}
                      editingCell={editingCell}
                      editingValue={editingValue}
                      setEditingValue={setEditingValue}
                      handleCommitEdit={readOnly ? () => {} : handleCommitEdit}
                      handleCancelEdit={handleCancelEdit}
                      handleCellActivate={effectiveHandleCellActivate}
                      handleOpenNode={handleOpenNode}
                      onNodeChange={readOnly ? null : handleNodeFieldChange}
                      typeKey={typeDef?.key}
                      selectedKeys={resolvedSelectedKeys}
                      showCheckboxes={showCheckboxes}
                      language={language}
                      resolveNodelistTitle={resolveNodelistTitle}
                      onToggleSelect={handleToggleSelect}
                      nodeDragEnabled={nodeDragEnabled && !readOnly}
                      onNodeDragStart={(event, node) => {
                            writeNodeDragData(event, node);
                      }}
                    />

                    <TableFooter
                      addLabel={addLabel}
                      hasMore={hasMore}
                      onAddRow={readOnly ? undefined : handleAddRow}
                      onLoadMore={handleLoadMore}
                    />

                    <TableModals
                      modalState={modalState}
                      fieldDataSection={fieldDataSection}
                      handleModalClose={handleModalClose}
                      handleEnumChange={handleEnumChange}
                      handleDateChange={handleDateChange}
                      handleTagsSaved={handleTagsSaved}
                      handleLocationSaved={handleLocationSaved}
                      handlePeopleSaved={handlePeopleSaved}
                      handleNodelistSaved={handleNodelistSaved}
                      handlePercentageChange={handlePercentageChange}
                      fieldModalOpen={fieldModalOpen}
                      handleFieldModalOpen={handleFieldModalOpen}
                      handleFieldSave={handleFieldSave}
                      fieldSaving={fieldSaving}
                    />

                </div>

            </div>

        </div>
    );

});

// Dev Tools
TableView.displayName = 'TableView';

export default TableView;
